import { AnimatePresence, MotionConfig, useReducedMotion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EVENTS, useJoyride, type EventHandler, type Props } from "react-joyride";

import { TOUR_EVENT, TOUR_HELP_TARGET, TOUR_SPOTLIGHT_PADDING_PX, TOUR_SPOTLIGHT_RADIUS_PX, TOUR_Z_INDEX } from "../constants";
import { watchScrollSettle } from "../scrollSettle";
import { getTourCommand, type TourDirection } from "../tourCommand";
import type { TourEventBody } from "../tourState";
import { getCanonicalStep, getResumeIndex, getStepTarget } from "../tourSteps";
import type { TourRunnerStep } from "../types";
import { useTour } from "../useTour";
import { useTourState } from "../useTourState";
import { useTourTargets } from "../useTourTarget";
import { TourCoachmark } from "./TourCoachmark";
import { TourSpotlight } from "./TourSpotlight";

const FOCUSABLE_SELECTOR = 'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const EDITABLE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);
const TARGET_WAIT_MS = 2000;

type TourRunnerProps = {
  steps: TourRunnerStep[];
  prepareStep: (stepId: string | undefined) => boolean;
  onEnd?: () => void;
};

const isEditableTarget = (target: EventTarget | null) =>
  target instanceof HTMLElement && (EDITABLE_TAGS.has(target.tagName) || target.isContentEditable);

const focusTarget = (element: HTMLElement | null) => {
  if (!element) return;
  const focusable = element.matches(FOCUSABLE_SELECTOR)
    ? element
    : element.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
  focusable?.focus();
};

export function TourRunner({ steps, prepareStep, onEnd }: TourRunnerProps) {
  const { definition, stepIds, fromCanonical, stop } = useTour();
  const targets = useTourTargets();
  const reduceMotion = useReducedMotion();
  const { record } = useTourState(definition);

  const [index, setIndex] = useState(() =>
    fromCanonical === null ? 0 : getResumeIndex(definition, stepIds, fromCanonical),
  );
  const [shownIndex, setShownIndex] = useState<number | null>(null);
  const direction = useRef<TourDirection>("next");
  const settledIndex = useRef<number | null>(null);
  const isEnded = useRef(false);
  const latest = useRef({ index, stepIds });

  useEffect(() => {
    latest.current = { index, stepIds };
  }, [index, stepIds]);

  const stepCount = stepIds.length;

  const goTo = useCallback((nextIndex: number, nextDirection: TourDirection) => {
    settledIndex.current = null;
    direction.current = nextDirection;
    setIndex(nextIndex);
  }, []);

  const gatedSteps = useMemo(
    () =>
      steps.map((step, stepIndex) => ({
        ...step,
        target: () => (settledIndex.current === stepIndex ? step.target() : null),
      })),
    [steps],
  );

  const end = useCallback(
    (body: TourEventBody) => {
      isEnded.current = true;
      record(body);
      const endedStepId = latest.current.stepIds[latest.current.index];
      stop();
      onEnd?.();
      requestAnimationFrame(() =>
        focusTarget(targets.get(getStepTarget(definition, endedStepId)) ?? targets.get(TOUR_HELP_TARGET)),
      );
    },
    [record, stop, onEnd, targets, definition],
  );

  useEffect(
    () => () => {
      if (isEnded.current) return;
      const stepId = latest.current.stepIds[latest.current.index];
      if (!stepId) {
        record({ event: TOUR_EVENT.COMPLETED });
        return;
      }
      record({ event: TOUR_EVENT.CLOSED, step: getCanonicalStep(definition, stepId) });
    },
    [record, definition],
  );

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.isComposing) return;
      event.preventDefault();
      end({ event: index >= stepCount ? TOUR_EVENT.COMPLETED : TOUR_EVENT.SKIPPED });
    };

    window.addEventListener("keydown", handleEscape, true);
    return () => window.removeEventListener("keydown", handleEscape, true);
  }, [end, index, stepCount]);

  useEffect(() => {
    const handleArrow = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isEditableTarget(event.target)) return;
      if (event.key === "ArrowLeft" && index > 0) {
        event.preventDefault();
        goTo(index - 1, "prev");
        return;
      }
      if (event.key === "ArrowRight" && index < stepCount) {
        event.preventDefault();
        goTo(index + 1, "next");
      }
    };

    document.addEventListener("keydown", handleArrow);
    return () => document.removeEventListener("keydown", handleArrow);
  }, [goTo, index, stepCount]);

  useEffect(() => {
    const stepId = stepIds[index];
    if (!prepareStep(stepId)) return;
    const target = targets.get(getStepTarget(definition, stepId));
    if (!target) {
      settledIndex.current = index;
      return;
    }
    target.scrollIntoView({ block: "center", behavior: reduceMotion ? "instant" : "smooth" });
    return watchScrollSettle(target, () => {
      settledIndex.current = index;
    });
  }, [index, stepIds, prepareStep, targets, definition, reduceMotion]);

  const handleEvent = useCallback<EventHandler>(
    (data) => {
      if (data.type === EVENTS.TARGET_NOT_FOUND) {
        console.warn("[tour] target not found:", stepIds[data.index] ?? "finish");
      }
      if (data.type === EVENTS.TOOLTIP) setShownIndex(data.index);
      const command = getTourCommand(data, stepCount, direction.current);
      if (!command) return;
      if (command.kind === "go") {
        goTo(command.index, command.direction);
        return;
      }
      end({ event: command.kind === "complete" ? TOUR_EVENT.COMPLETED : TOUR_EVENT.SKIPPED });
    },
    [end, goTo, stepCount, stepIds],
  );

  const joyrideProps = useMemo<Props>(
    () => ({
      run: true,
      continuous: true,
      steps: gatedSteps,
      stepIndex: index,
      onEvent: handleEvent,
      tooltipComponent: TourCoachmark,
      loaderComponent: null,
      options: {
        skipBeacon: true,
        blockTargetInteraction: true,
        overlayClickAction: false,
        dismissKeyAction: false,
        targetWaitTimeout: TARGET_WAIT_MS,
        zIndex: TOUR_Z_INDEX,
        offset: 12,
        spotlightPadding: TOUR_SPOTLIGHT_PADDING_PX,
        spotlightRadius: TOUR_SPOTLIGHT_RADIUS_PX,
        overlayColor: "var(--overlay)",
        backgroundColor: "var(--popover)",
        arrowColor: "var(--popover)",
        textColor: "var(--popover-foreground)",
        primaryColor: "var(--primary)",
      },
    }),
    [gatedSteps, index, handleEvent],
  );

  const { Tour } = useJoyride(joyrideProps);
  const spotlightTarget = shownIndex === index ? steps[index]?.target() : null;

  return (
    <MotionConfig reducedMotion="user">
      {Tour}
      <AnimatePresence>{spotlightTarget && <TourSpotlight key={index} target={spotlightTarget} />}</AnimatePresence>
    </MotionConfig>
  );
}
